import React from "react";
import { Transition } from "@headlessui/react";
import { MerchantButton, Checkbox } from "@components";
import { formatAmount } from "@utils";
import { TagIcon } from "@heroicons/react/16/solid";

const SummaryButton = ({
  errorMessage,
  currentAmount,
  passOnCorporateCardFee,
  isCorporate,
  cardIsAmex,
  amexCorporateConfirmed,
  setAmexCorporateConfirmed,
  handleContinue,
  processing,
  loading,
  disablePayment,
  merchant,
  hasStatement,
  summaryData,
  settlementDiscount = 0,
}) => {
  const { invoiceCount = 0 } = summaryData || {};
  const invoiceDisplayAmount = currentAmount
    ? currentAmount.baseAmount + settlementDiscount
    : null;

  return (
    <>
      {!!errorMessage ? (
        <div className="flex items-center gap-2">
          <div className="flex-shrink-0 w-5 h-5 mt-0.5">
            <div className="w-5 h-5 border-red-600 border rounded-full flex items-center justify-center">
              <span className="text-red-600 text-sm font-bold">!</span>
            </div>
          </div>
          <div className="text-red-600 text-xs/5">{errorMessage}</div>
        </div>
      ) : (
        <>
          <Transition
            show={!!currentAmount && passOnCorporateCardFee && isCorporate}
            appear
            enter="transition-opacity duration-300 ease-in"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-300 ease-in"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="overflow-hidden">
              {isCorporate && (
                <div className="space-y-2 py-2 lg:py-0">
                  <div className="flex justify-between items-center">
                    {hasStatement ? (
                      <span className="text-zinc-600 font-light text-sm">
                        {invoiceCount}{" "}
                        {invoiceCount !== 1 ? "invoices" : "invoice"}
                      </span>
                    ) : (
                      <span className="text-zinc-600 font-light text-sm">
                        Amount
                      </span>
                    )}
                    <span className="font-normal lg:font-light text-sm">
                      {invoiceDisplayAmount?.toLocaleString("en-GB", { style: "currency", currency: "GBP" })}
                    </span>
                  </div>
                  {settlementDiscount !== 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-600 font-light text-sm flex items-center gap-1">
                        <TagIcon className="size-3.5 shrink-0" />
                        Settlement discount
                      </span>
                      <span className={`font-light text-sm ${settlementDiscount > 0 ? "text-green-700" : "text-zinc-600"}`}>
                        {settlementDiscount > 0 ? "-" : "+"}{formatAmount(Math.abs(settlementDiscount))}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-600 font-light text-sm">
                      {`Corporate card fee (${currentAmount?.totalFeesPercentage}%)`}
                    </span>
                    <span className="font-normal lg:font-light text-sm">
                      £
                      {currentAmount?.totalFeesAmount.toLocaleString("en-GB", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  {/* "You will pay" section - only show on lg: screens */}
                  <div className="hidden lg:flex justify-between items-center py-2.5 lg:py-4 font-normal border-t border-zinc-600">
                    <span>You will pay</span>
                    <span>
                      {currentAmount &&
                        formatAmount(
                          passOnCorporateCardFee
                            ? currentAmount.totalAmount
                            : currentAmount.baseAmount
                        )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </Transition>

          {cardIsAmex && isCorporate && (
            <button
              className="flex items-center p-3 rounded-xl border border-zinc-200 mt-2 cursor-pointer hover:bg-zinc-50 w-full"
              onClick={() => setAmexCorporateConfirmed(!amexCorporateConfirmed)}
            >
              <Checkbox checked={amexCorporateConfirmed} />
              <span className="text-sm pl-2">
                I confirm I'm using an AMEX corporate card
              </span>
            </button>
          )}
          {(!isCorporate || !passOnCorporateCardFee) && (
            <div className="py-2 lg:py-0">
              <div className="flex justify-between items-center mb-2 lg:mb-3">
                {hasStatement ? (
                  <span className="text-zinc-600 font-light text-sm">
                    {summaryData?.invoiceCount}{" "}
                    {summaryData?.invoiceCount !== 1 ? "invoices" : "invoice"}
                  </span>
                ) : (
                  <span className="text-zinc-600 font-light text-sm">
                    Amount
                  </span>
                )}
                <span className="font-normal lg:font-light text-sm">
                  {invoiceDisplayAmount?.toLocaleString("en-GB", { style: "currency", currency: "GBP" })}
                </span>
              </div>
              {settlementDiscount !== 0 && (
                <div className="flex justify-between items-center mb-2 lg:mb-3">
                  <span className="text-zinc-600 font-light text-sm flex items-center gap-1">
                    <TagIcon className="size-3.5 shrink-0" />
                    Settlement discount
                  </span>
                  <span className={`font-light text-sm ${settlementDiscount > 0 ? "text-green-700" : "text-zinc-600"}`}>
                    {settlementDiscount > 0 ? "-" : "+"}{formatAmount(Math.abs(settlementDiscount))}
                  </span>
                </div>
              )}
              {/* "You will pay" section - only show on lg: screens */}
              <div className="hidden lg:flex justify-between items-center py-2.5 lg:py-4 font-normal border-t border-zinc-600">
                <span>You will pay</span>
                <span>
                  {currentAmount &&
                    formatAmount(
                      passOnCorporateCardFee
                        ? currentAmount.totalAmount
                        : currentAmount.baseAmount
                    )}
                </span>
              </div>
            </div>
          )}
        </>
      )}
      <MerchantButton
        onClick={handleContinue}
        disabled={disablePayment}
        loading={processing || loading}
        merchant={merchant}
        className="w-full mt-4"
      >
        Pay
        {!loading &&
          currentAmount &&
          " " +
            formatAmount(
              passOnCorporateCardFee
                ? currentAmount.totalAmount
                : currentAmount.baseAmount
            )}
      </MerchantButton>
    </>
  );
};

export default SummaryButton;
